import { useEffect, useState } from "react";

import { useParams, useSearchParams } from "react-router-dom";

import { AlertTriangle } from "lucide-react";

import { Alert, AlertDescription } from "~/components/ui/alert";
import { Card } from "~/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import { ListSkeleton, StatCardsSkeleton } from "~/components/ui/skeletons";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { useClientDetail } from "~/hooks/useClientDetail";
import ClientCuentasPorCobrarTab from "~/pages/clients/components/client-detail/ClientCuentasPorCobrarTab";
import ClientHeader from "~/pages/clients/components/client-detail/ClientHeader";
import ClientInfoTab from "~/pages/clients/components/client-detail/ClientInfoTab";
import ClientNotFound from "~/pages/clients/components/client-detail/ClientNotFound";
import InternalNotesTab from "~/pages/clients/components/client-detail/InternalNotesTab";
import { PaymentProofsTab } from "~/pages/clients/components/client-detail/PaymentProofsTab";
import PaymentsTab from "~/pages/clients/components/client-detail/PaymentsTab";
import SalesRequestsTab from "~/pages/clients/components/client-detail/SalesRequestsTab";
import AddressesTab from "~/pages/clients/components/client-detail/address/AddressesTab";
import ClientCotizacionesTab from "~/pages/clients/components/client-detail/cotizaciones/ClientCotizacionesTab";
import { useProfileStore } from "~/store/profileStore";

const tabContentClass =
  "animate-in fade-in-0 slide-in-from-bottom-1 duration-300";

const ClientDetailPage = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const [activeTab, setActiveTab] = useState("info");
  const { client, clientOrders, loading, refreshClient } =
    useClientDetail(clientId);
  const { role } = useProfileStore();
  const isSeller = role === "seller";
  const [searchParams] = useSearchParams();
  const salesRequestId = searchParams.get("sales_request");

  // Check URL parameters on component mount to auto-switch tab
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get("tab");

    if (tabParam) {
      setActiveTab(tabParam);

      // Clean up URL parameter
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete("tab");
      window.history.replaceState({}, "", newUrl.toString());
    }
  }, []);

  // Listen for client-updated events
  useEffect(() => {
    const handleClientUpdated = (event: CustomEvent<{ clientId: string }>) => {
      // Only refresh if this is the client we're currently viewing
      if (event.detail.clientId === clientId) {
        refreshClient();
      }
    };

    // Add event listener with type assertion
    window.addEventListener(
      "client-updated",
      handleClientUpdated as EventListener
    );

    // Clean up
    return () => {
      window.removeEventListener(
        "client-updated",
        handleClientUpdated as EventListener
      );
    };
  }, [clientId, refreshClient]);

  // Listen for tab switch events
  useEffect(() => {
    const handleTabSwitch = (event: CustomEvent<{ tabName: string }>) => {
      setActiveTab(event.detail.tabName);
    };

    // Add event listener with type assertion
    window.addEventListener(
      "switch-client-tab",
      handleTabSwitch as EventListener
    );

    // Clean up
    return () => {
      window.removeEventListener(
        "switch-client-tab",
        handleTabSwitch as EventListener
      );
    };
  }, []);

  // Set active tab to 'sales-requests' if sales_request param is present
  useEffect(() => {
    if (salesRequestId) {
      setActiveTab("sales-requests");
    }
  }, [salesRequestId]);

  // Handle localStorage for reopening dialog after address updates
  useEffect(() => {
    const reopenOrderId = localStorage.getItem("reopenOrderDialog");

    if (reopenOrderId) {
      // The SalesRequestsTab will handle opening the dialog via salesRequestId prop
      // Clean up localStorage
      localStorage.removeItem("reopenOrderDialog");
    }
  }, []);

  if (loading) {
    return (
      <div className="w-full max-w-[100vw] space-y-6 px-4 md:max-w-[70vw] lg:max-w-[80vw] lg:px-10 xl:px-0">
        {/* Client header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-40" />
          </div>
          <Skeleton className="h-9 w-40" />
        </div>

        <Card className="overflow-hidden rounded-2xl shadow-md">
          <div className="flex gap-2 p-4">
            {Array.from({ length: isSeller ? 6 : 8 }).map((_, i) => (
              <Skeleton key={i} className="h-9 flex-1" />
            ))}
          </div>
          <div className="space-y-4 px-4 pb-6 md:px-2">
            <StatCardsSkeleton count={4} />
            <ListSkeleton rows={5} />
          </div>
        </Card>
      </div>
    );
  }

  if (!client) {
    return <ClientNotFound />;
  }

  return (
    <div className="w-full max-w-[100vw] space-y-6 px-4 md:max-w-[70vw] lg:max-w-[80vw] lg:px-10 xl:px-0">
      {client.deleted_at && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Cliente eliminado el{" "}
            {new Date(client.deleted_at).toLocaleDateString("es-ES", {
              year: "numeric",
              month: "long",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
            .
          </AlertDescription>
        </Alert>
      )}
      <ClientHeader client={client} />
      <Card className="overflow-hidden rounded-2xl shadow-md">
        {isSeller ? (
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <div className="p-4 md:hidden">
              <Select value={activeTab} onValueChange={setActiveTab}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Seleccionar vista">
                    {activeTab === "info" && "Información"}
                    {activeTab === "sales-requests" && "Notas de Venta"}
                    {activeTab === "cotizaciones" && "Cotizaciones"}
                    {activeTab === "internal-notes" && "Notas Internas"}
                    {activeTab === "cuentas-por-cobrar" && "Cuentas por Cobrar"}
                    {activeTab === "payment-proofs" && "Comprobantes"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="info">Información</SelectItem>
                  <SelectItem value="sales-requests">Notas de Venta</SelectItem>
                  <SelectItem value="cotizaciones">Cotizaciones</SelectItem>
                  <SelectItem value="internal-notes">Notas Internas</SelectItem>
                  <SelectItem value="cuentas-por-cobrar">
                    Cuentas por Cobrar
                  </SelectItem>
                  <SelectItem value="payment-proofs">Comprobantes</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <TabsList className="mb-2 hidden h-auto w-full p-1 md:flex">
              <TabsTrigger
                value="info"
                className="flex-1 whitespace-nowrap px-2 py-2.5 text-xs sm:text-sm"
              >
                Info
              </TabsTrigger>
              <TabsTrigger
                value="sales-requests"
                className="flex-1 whitespace-nowrap px-2 py-2.5 text-xs sm:text-sm"
              >
                Notas
              </TabsTrigger>
              <TabsTrigger
                value="cotizaciones"
                className="flex-1 whitespace-nowrap px-2 py-2.5 text-xs sm:text-sm"
              >
                Cotizaciones
              </TabsTrigger>
              <TabsTrigger
                value="internal-notes"
                className="flex-1 whitespace-nowrap px-2 py-2.5 text-xs sm:text-sm"
              >
                Internas
              </TabsTrigger>
              <TabsTrigger
                value="cuentas-por-cobrar"
                className="flex-1 whitespace-nowrap px-2 py-2.5 text-xs sm:text-sm"
              >
                Cuentas
              </TabsTrigger>
              <TabsTrigger
                value="payment-proofs"
                className="flex-1 whitespace-nowrap px-2 py-2.5 text-xs sm:text-sm"
              >
                Comprobantes
              </TabsTrigger>
            </TabsList>

            <div className="px-4 md:px-2">
              <TabsContent value="info" className={tabContentClass}>
                <ClientInfoTab client={client} refreshClient={refreshClient} />
              </TabsContent>
              <TabsContent value="sales-requests" className={tabContentClass}>
                <SalesRequestsTab
                  clientId={clientId || ""}
                  salesRequestId={salesRequestId}
                />
              </TabsContent>
              <TabsContent value="cotizaciones" className={tabContentClass}>
                <ClientCotizacionesTab clientId={clientId || ""} />
              </TabsContent>
              <TabsContent value="internal-notes" className={tabContentClass}>
                <InternalNotesTab
                  client={client}
                  onClientUpdated={refreshClient}
                  readOnly
                />
              </TabsContent>
              <TabsContent
                value="cuentas-por-cobrar"
                className={tabContentClass}
              >
                <ClientCuentasPorCobrarTab clientId={clientId || ""} />
              </TabsContent>
              <TabsContent value="payment-proofs" className={tabContentClass}>
                <PaymentProofsTab clientId={clientId || ""} />
              </TabsContent>
            </div>
          </Tabs>
        ) : (
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <div className="p-4 md:hidden">
              <Select value={activeTab} onValueChange={setActiveTab}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Seleccionar vista">
                    {activeTab === "info" && "Información"}
                    {activeTab === "addresses" && "Direcciones"}
                    {activeTab === "sales-requests" && "Notas de Venta"}
                    {activeTab === "cotizaciones" && "Cotizaciones"}
                    {activeTab === "internal-notes" && "Notas Internas"}
                    {activeTab === "cuentas-por-cobrar" && "Cuentas por Cobrar"}
                    {activeTab === "payments" && "Pagos"}
                    {activeTab === "payment-proofs" && "Comprobantes"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="info">Información</SelectItem>
                  <SelectItem value="addresses">Direcciones</SelectItem>
                  <SelectItem value="sales-requests">Notas de Venta</SelectItem>
                  <SelectItem value="cotizaciones">Cotizaciones</SelectItem>
                  <SelectItem value="internal-notes">Notas Internas</SelectItem>
                  <SelectItem value="cuentas-por-cobrar">
                    Cuentas por Cobrar
                  </SelectItem>
                  <SelectItem value="payments">Pagos</SelectItem>
                  <SelectItem value="payment-proofs">Comprobantes</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <TabsList className="hidden w-full grid-cols-8 px-6 md:grid">
              <TabsTrigger value="info">Información</TabsTrigger>
              <TabsTrigger value="addresses">Direcciones</TabsTrigger>
              <TabsTrigger value="sales-requests">Notas de Venta</TabsTrigger>
              <TabsTrigger value="cotizaciones">Cotizaciones</TabsTrigger>
              <TabsTrigger value="internal-notes">Notas Internas</TabsTrigger>
              <TabsTrigger value="cuentas-por-cobrar">Cuentas</TabsTrigger>
              <TabsTrigger value="payments">Pagos</TabsTrigger>
              <TabsTrigger value="payment-proofs">Comprobantes</TabsTrigger>
            </TabsList>

            <div className="px-4 md:px-2">
              <TabsContent value="info" className={tabContentClass}>
                <ClientInfoTab client={client} refreshClient={refreshClient} />
              </TabsContent>
              <TabsContent value="addresses" className={tabContentClass}>
                <AddressesTab />
              </TabsContent>
              <TabsContent value="sales-requests" className={tabContentClass}>
                <SalesRequestsTab
                  clientId={clientId || ""}
                  salesRequestId={salesRequestId}
                />
              </TabsContent>
              <TabsContent value="cotizaciones" className={tabContentClass}>
                <ClientCotizacionesTab clientId={clientId || ""} />
              </TabsContent>
              <TabsContent value="internal-notes" className={tabContentClass}>
                <InternalNotesTab
                  client={client}
                  onClientUpdated={refreshClient}
                />
              </TabsContent>
              <TabsContent
                value="cuentas-por-cobrar"
                className={tabContentClass}
              >
                <ClientCuentasPorCobrarTab clientId={clientId || ""} />
              </TabsContent>
              <TabsContent value="payments" className={tabContentClass}>
                <PaymentsTab clientOrders={clientOrders} />
              </TabsContent>
              <TabsContent value="payment-proofs" className={tabContentClass}>
                <PaymentProofsTab clientId={clientId || ""} />
              </TabsContent>
            </div>
          </Tabs>
        )}
      </Card>
    </div>
  );
};

export default ClientDetailPage;
